import * as React from 'react';

import { cva } from 'class-variance-authority';

import { cn } from '../../../../lib/stylingUtils';

const cardViewVariants = cva(['grid w-full grid-cols-12 gap-4 p-0']);

const cardContainerVariants = cva([
  'col-span-12 sm:col-span-6 md:col-span-6 lg:col-span-4 xl:col-span-3',
]);

/**
 * Props for the CardView component.
 *
 * @template T - The type of items to be displayed in the card view
 */
export interface CardViewProps<T> extends React.PropsWithChildren {
  /**
   * Array of items to render as cards
   */
  items: T[];

  /**
   * Function to render an individual item as a card component
   * @param key - The index of the item in the array
   * @param item - The item to render
   * @returns React node representing the card content
   */
  renderItem: (key: number, item: T) => React.ReactNode;

  /**
   * Additional CSS class names to apply to the card view container
   */
  className?: string;

  /**
   * Additional CSS class names to apply to each card container (not the card itself)
   */
  cardClassName?: string;

  /**
   * Text to display when the items array is empty
   */
  emptyDescription?: string;
}

/**
 * CardView component for displaying a responsive grid of cards.
 *
 * Renders a collection of items as cards in a responsive grid layout.
 * Accepts a render function to customize how each item is displayed.
 *
 * @template T - The type of items to be displayed in the card view
 *
 * @example
 * ```tsx
 * // Basic usage with an array of items
 * <CardView
 *   items={users}
 *   renderItem={(index, user) => (
 *     <Card>
 *       <h3>{user.name}</h3>
 *       <p>{user.email}</p>
 *     </Card>
 *   )}
 * />
 *
 * // With custom styles
 * <CardView
 *   items={products}
 *   className="gap-6"
 *   cardClassName="lg:col-span-6"
 *   renderItem={(index, product) => (
 *     <Card onClick={() => selectProduct(product.id)}>
 *       <h3>{product.name}</h3>
 *       <p>${product.price}</p>
 *     </Card>
 *   )}
 * />
 * ```
 *
 * @param props - CardView properties
 * @returns Rendered CardView component
 */
export const CardView = <T,>({
  className,
  items,
  renderItem,
  cardClassName,
  // emptyDescription,
  // children,
}: CardViewProps<T>) => (
  <div className={cn('@CardView', cardViewVariants({ className }))}>
    {items.map((item, index) => (
      <div
        key={index}
        className={cardContainerVariants({ className: cardClassName })}
      >
        {renderItem(index, item)}
      </div>
    ))}
  </div>
);

export { Card } from './Card';
